import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";

import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";

import { useApiQuery, resolveFileUrl } from "../api/client";

import "./VideoSection.css";

const VideoSection = () => {
  const { ref, inView } = useInView({ threshold: 0.14, triggerOnce: true });
  const [active, setActive] = useState(null);

  const { data, isLoading, isError } = useApiQuery("/videos?type=update");
  const videos = data || [];

  const openVideo = (v) => setActive(v);
  const closeVideo = () => setActive(null);

  const latestDate = videos.length > 0 ? videos[0].date : "";

  return (
    <section className="vs" id="videos" ref={ref}>
      <div className="vs__container">
        {/* Heading */}
        <motion.div
          className="vs__head"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <div className="vs__kicker">VIDEO UPDATES</div>
          <h2 className="vs__title">
            Short, real updates — straight from the site.
          </h2>

          <div className="vs__meta">
            <span className="vs__pill">
              <VideocamRoundedIcon /> Weekly / monthly updates
            </span>
            {latestDate && (
              <span className="vs__pill">
                <CalendarMonthRoundedIcon /> Latest: {latestDate}
              </span>
            )}
          </div>
        </motion.div>

        {/* Grid */}
        <motion.div
          className="vs__grid"
          initial={{ opacity: 0, y: 14 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.08 }}
        >
          {isLoading && <div className="vs__state">Loading videos…</div>}
          {isError && <div className="vs__state">Couldn't load videos right now.</div>}
          {!isLoading && !isError && videos.length === 0 && (
            <div className="vs__state">No videos posted yet — check back soon.</div>
          )}

          {videos.map((v) => (
            <button
              key={v._id}
              type="button"
              className="vs__card"
              onClick={() => openVideo(v)}
            >
              <div className="vs__thumbWrap">
                {v.thumbUrl ? (
                  <img
                    className="vs__thumb"
                    src={resolveFileUrl(v.thumbUrl)}
                    alt={v.title}
                  />
                ) : (
                  <video
                    className="vs__thumb"
                    src={resolveFileUrl(v.videoUrl)}
                    muted
                    preload="metadata"
                  />
                )}
                <div className="vs__shade" />
                <div className="vs__play">
                  <PlayArrowRoundedIcon />
                </div>
              </div>

              <div className="vs__body">
                <div className="vs__name">{v.title}</div>
                <div className="vs__date">{v.date}</div>
              </div>
            </button>
          ))}
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            className="vs__modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="vs__backdrop" onClick={closeVideo} />

            <motion.div
              className="vs__modalCard"
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 18, scale: 0.98 }}
              transition={{ duration: 0.22 }}
            >
              <div className="vs__modalTop">
                <div>
                  <div className="vs__modalTitle">{active.title}</div>
                  <div className="vs__modalSub">{active.date}</div>
                </div>

                <button
                  className="vs__close"
                  onClick={closeVideo}
                  type="button"
                >
                  <CloseRoundedIcon />
                </button>
              </div>

              <div className="vs__player">
                <video
                  src={resolveFileUrl(active.videoUrl)}
                  controls
                  autoPlay
                  playsInline
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default VideoSection;
